// Copyright (c) 2026 Michael F. Kamprath
//
// count-model.js -- Round-level count state for Learn (and later Quiz) mode.
//
// The model tracks:
//   - current count (0..objectCount, 1-indexed on increment)
//   - completion (count reached objectCount)
//   - onIncrement / onComplete callbacks
//
// Per-sprite "already counted" state is intentionally NOT owned by this
// model.  That concern lives on the sprite container (`_counted` flag
// on each sprite) because it's tightly coupled to the tap handler's
// early-exit logic and survives manager reconstruction via the
// early-tap buffer.  The model is about the round's aggregate count;
// it doesn't know which sprites were tapped, only how many.
//
// Usage pattern:
//
//   const countModel = createCountModel({
//     objectCount: sprites.length,
//     onComplete: () => stateMachine.transition('LEARN_SUCCESS'),
//   });
//   ...
//   const value = countModel.increment();  // 1, 2, 3, ...
//   if (value === null) return;            // already complete or destroyed
//
// The model's completion callback fires SYNCHRONOUSLY after the
// increment that brought count to objectCount.  Callers that need to
// delay round-end sequencing (to let the final plaque animation
// finish, play the success praise, etc.) should handle that in their
// callback body -- the model just signals "all counted" and gets out
// of the way.

/**
 * @typedef {Object} CountModel
 * @property {() => number} getCount         Current count, starts at 0.
 * @property {() => number} getObjectCount   Total count to reach completion.
 * @property {() => boolean} isComplete      True once count === objectCount.
 * @property {() => number | null} increment Returns new count, or null if
 *                                           already complete / destroyed.
 * @property {() => void} reset              Reset count to 0.  onComplete
 *                                           can fire again after a reset.
 * @property {() => void} destroy            Refuse further increments.
 */

/**
 * @param {Object} opts
 * @param {number} opts.objectCount
 *   Target count at which isComplete() becomes true.  Must be a positive
 *   integer.
 * @param {(value: number) => void} [opts.onIncrement]
 *   Fires synchronously on every successful increment, BEFORE onComplete.
 * @param {(value: number) => void} [opts.onComplete]
 *   Fires synchronously once, on the increment that brings count to
 *   objectCount.  Does not fire again until reset() is called.
 * @returns {CountModel}
 */
export function createCountModel({ objectCount, onIncrement, onComplete } = {}) {
  if (!Number.isInteger(objectCount) || objectCount < 1) {
    throw new TypeError(
      `createCountModel: objectCount must be a positive integer (got ${objectCount})`,
    );
  }

  let count = 0;
  let destroyed = false;
  let completionFired = false;

  function fireCallback(fn, value, label) {
    if (typeof fn !== 'function') return;
    try {
      fn(value);
    } catch (err) {
      if (import.meta.env?.DEV) {
        // eslint-disable-next-line no-console
        console.error(`[count-model] ${label} callback threw:`, err);
      }
    }
  }

  return {
    getCount() { return count; },
    getObjectCount() { return objectCount; },
    isComplete() { return completionFired; },

    increment() {
      if (destroyed) return null;
      if (completionFired) return null;
      count += 1;
      const value = count;
      fireCallback(onIncrement, value, 'onIncrement');
      if (count >= objectCount) {
        completionFired = true;
        fireCallback(onComplete, value, 'onComplete');
      }
      return value;
    },

    reset() {
      count = 0;
      completionFired = false;
    },

    destroy() {
      destroyed = true;
    },
  };
}
